/**
 * Environment identification — the banner that tells a person which system they are looking at.
 *
 * Requested 2026-08-17: demo, test and sandbox environments must carry a visible flag and be easily
 * distinguishable from production at a glance.
 *
 * The request said those environments should be flagged "No PHI". That is right for two of the three
 * and **wrong for the third**, which is why this module derives the message from the environment's
 * class rather than hardcoding one string:
 *
 *   - Demo and sandbox are synthetic-only, so "No PHI" is true and worth saying loudly.
 *   - **Subscriber test environments hold real patient data** (`emr-po` C12). Labelling one "No PHI"
 *     would be false in the most dangerous possible direction — it tells someone the data is safe to
 *     treat casually, screenshot, or copy, when it is real PHI. So that environment gets the opposite
 *     warning.
 *
 * A banner that can be wrong is worse than no banner, because people stop reading the words and start
 * reading the presence of a coloured bar. Hence: one source of truth, derived, and tested.
 */

/**
 * What kind of system this is. Set from configuration at startup and never inferred from a hostname —
 * a hostname is a deployment detail that changes, and getting this wrong is the whole risk.
 */
export const ENVIRONMENT_KINDS = [
  'production',
  'subscriber-test',
  'demo',
  'sandbox',
  'local',
] as const;
export type EnvironmentKind = (typeof ENVIRONMENT_KINDS)[number];

/**
 * Whether an environment may contain real patient data.
 *
 * This is the property everything else hangs off, and it is deliberately a total map rather than a
 * predicate with a default — adding a kind without deciding its data class becomes a type error rather
 * than an inherited guess.
 */
const HOLDS_REAL_PHI: Record<EnvironmentKind, boolean> = {
  production: true,
  /** Holds a copy of the customer's real data. A production-grade environment that is merely named "test". */
  'subscriber-test': true,
  demo: false,
  sandbox: false,
  local: false,
};

export function holdsRealPhi(kind: EnvironmentKind): boolean {
  return HOLDS_REAL_PHI[kind];
}

/** How prominent the banner should be. */
export type BadgeTone =
  /** Real patient data in a non-production system — the case people most need warning about. */
  | 'danger'
  /** Synthetic data. Informational: you cannot break anything, and nothing here is real. */
  | 'info'
  /** Production. No banner. */
  | 'none';

export interface EnvironmentBadge {
  readonly kind: EnvironmentKind;
  /** Whether to render anything at all. */
  readonly visible: boolean;
  readonly tone: BadgeTone;
  /** Short, all-caps, readable at a glance from across a room during a demo. */
  readonly label: string;
  /** One sentence a person can act on. */
  readonly detail: string;
  /** True when the environment contains real patient data, whatever its name suggests. */
  readonly holdsRealPhi: boolean;
}

/**
 * Production deliberately shows nothing.
 *
 * Tempting to badge everything "for consistency", but a banner present on every environment carries no
 * information — the eye stops seeing it, which is exactly when the demo one stops working. The signal
 * is the *exception*, so production is the silent case.
 */
const BADGES: Record<EnvironmentKind, Omit<EnvironmentBadge, 'kind' | 'holdsRealPhi'>> = {
  production: {
    visible: false,
    tone: 'none',
    label: '',
    detail: '',
  },
  'subscriber-test': {
    visible: true,
    tone: 'danger',
    label: 'TEST — REAL PATIENT DATA',
    detail:
      'This is a test environment containing real patient data. Everything you do here is on live PHI: do not screenshot, export, or share.',
  },
  demo: {
    visible: true,
    tone: 'info',
    label: 'DEMO — NO PHI',
    detail:
      'Demonstration environment with synthetic patients only. Never enter real patient information here.',
  },
  sandbox: {
    visible: true,
    tone: 'info',
    label: 'SANDBOX — NO PHI',
    detail:
      'API sandbox with synthetic data only. Credentials here cannot reach production. Never send real patient information.',
  },
  local: {
    visible: true,
    tone: 'info',
    label: 'LOCAL — NO PHI',
    detail: 'Local development environment with synthetic data only.',
  },
};

export function environmentBadge(kind: EnvironmentKind): EnvironmentBadge {
  const badge = BADGES[kind];
  return { ...badge, kind, holdsRealPhi: holdsRealPhi(kind) };
}

export function isEnvironmentKind(value: string): value is EnvironmentKind {
  return (ENVIRONMENT_KINDS as readonly string[]).includes(value);
}

/**
 * Parse the configured kind, failing closed.
 *
 * An unrecognised or missing value resolves to `subscriber-test` — the most restrictive *visible*
 * kind. Deliberately not `production`, which would show no banner at all, and deliberately not `demo`,
 * which would claim "NO PHI" about an environment we know nothing about. When the configuration is
 * broken, the safe lie is "this might contain real data", never "this is safe".
 */
export function parseEnvironmentKind(value: string | undefined): EnvironmentKind {
  if (value !== undefined && isEnvironmentKind(value)) return value;
  return 'subscriber-test';
}
